import * as THREE from 'three';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls.js';
import { CSS3DRenderer } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
// Utility functions for Three.js setup and management
export class ThreeUtils {
    static createScene() {
        return new THREE.Scene();
    }
    static createCamera(width, height) {
        const camera = new THREE.PerspectiveCamera(75, width / height, 1, 10000);
        camera.position.set(0, 0, 1000);
        return camera;
    }
    static createWebGLRenderer(container) {
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setSize(container.clientWidth, container.clientHeight);
        renderer.domElement.style.pointerEvents = 'none';
        return renderer;
    }
    static createCSS3DRenderer(container) {
        const renderer = new CSS3DRenderer();
        renderer.setSize(container.clientWidth, container.clientHeight);
        renderer.domElement.style.position = 'absolute';
        renderer.domElement.style.top = '0';
        renderer.domElement.style.left = '0';
        renderer.domElement.style.zIndex = '1';
        renderer.domElement.style.pointerEvents = 'auto';
        return renderer;
    }
    static createTrackballControls(camera, domElement) {
        const controls = new TrackballControls(camera, domElement);
        controls.rotateSpeed = 0.5;
        controls.minDistance = 500;
        controls.maxDistance = 6000;
        controls.noRoll = true;
        controls.staticMoving = true;
        controls.dynamicDampingFactor = 0.15;
        return controls;
    }
    static createVideoElement(videoData) {
        const container = document.createElement('div');
        container.classList.add('css3d-card');
        container.style.pointerEvents = 'auto';
        container.style.width = `${videoData.size.width}px`;
        container.style.height = `${videoData.size.height}px`;
        container.style.backgroundColor = '#000';
        container.style.border = '2px solid #fff';
        container.style.borderRadius = '8px';
        container.style.overflow = 'hidden';
        container.style.cursor = 'pointer';
        container.style.position = 'relative';
        container.style.backfaceVisibility = 'hidden';
        container.style.webkitBackfaceVisibility = 'hidden';
        container.style.transformStyle = 'preserve-3d';
        container.style.willChange = 'transform';
        // Thumbnail image background
        const thumb = document.createElement('div');
        thumb.style.position = 'absolute';
        thumb.style.top = '0';
        thumb.style.left = '0';
        thumb.style.right = '0';
        thumb.style.bottom = '0';
        thumb.style.backgroundImage = `url(${videoData.thumbnailUrl})`;
        thumb.style.backgroundSize = 'cover';
        thumb.style.backgroundPosition = 'center';
        thumb.style.backfaceVisibility = 'hidden';
        thumb.style.webkitBackfaceVisibility = 'hidden';
        // Play overlay
        const overlay = document.createElement('div');
        overlay.style.position = 'absolute';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.right = '0';
        overlay.style.bottom = '0';
        overlay.style.display = 'flex';
        overlay.style.alignItems = 'center';
        overlay.style.justifyContent = 'center';
        overlay.style.background = 'linear-gradient(to bottom, rgba(0,0,0,0.0), rgba(0,0,0,0.3))';
        overlay.style.pointerEvents = 'none';
        overlay.style.backfaceVisibility = 'hidden';
        overlay.style.webkitBackfaceVisibility = 'hidden';
        const play = document.createElement('div');
        play.style.width = '0';
        play.style.height = '0';
        play.style.borderLeft = '22px solid #fff';
        play.style.borderTop = '14px solid transparent';
        play.style.borderBottom = '14px solid transparent';
        play.style.opacity = '0.9';
        overlay.appendChild(play);
        const title = document.createElement('div');
        title.textContent = videoData.title;
        title.style.position = 'absolute';
        title.style.bottom = '0';
        title.style.left = '0';
        title.style.right = '0';
        title.style.background = 'rgba(0,0,0,0.8)';
        title.style.color = 'white';
        title.style.padding = '8px';
        title.style.fontSize = '12px';
        title.style.maxHeight = '40px';
        title.style.overflow = 'hidden';
        title.style.pointerEvents = 'none';
        title.style.backfaceVisibility = 'hidden';
        title.style.webkitBackfaceVisibility = 'hidden';
        container.appendChild(thumb);
        container.appendChild(overlay);
        container.appendChild(title);
        // Flip hint badge (shown when not interactive from current angle)
        const hint = document.createElement('div');
        hint.className = 'flip-hint';
        hint.textContent = 'Rotate to view';
        hint.style.position = 'absolute';
        hint.style.top = '6px';
        hint.style.right = '6px';
        hint.style.padding = '2px 6px';
        hint.style.borderRadius = '4px';
        hint.style.fontSize = '11px';
        hint.style.background = 'rgba(0,0,0,0.65)';
        hint.style.color = '#fff';
        hint.style.pointerEvents = 'none';
        hint.style.display = 'none';
        container.appendChild(hint);
        let isPlaying = false;
        const playVideo = () => {
            if (isPlaying)
                return;
            isPlaying = true;
            // Notify scene to focus camera
            container.dispatchEvent(new CustomEvent('video-play', { detail: { id: videoData.id }, bubbles: true }));
            // Replace content with iframe to play
            container.innerHTML = '';
            const iframe = document.createElement('iframe');
            const origin = window.location.origin;
            const sep = videoData.url.includes('?') ? '&' : '?';
            const src = `${videoData.url}${sep}autoplay=1&rel=0&enablejsapi=1&playsinline=1&origin=${encodeURIComponent(origin)}`;
            iframe.src = src;
            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.style.border = 'none';
            iframe.setAttribute('allowfullscreen', '');
            iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
            iframe.style.backfaceVisibility = 'hidden';
            iframe.style.webkitBackfaceVisibility = 'hidden';
            container.appendChild(iframe);
            // Close button
            const closeBtn = document.createElement('button');
            closeBtn.textContent = '×';
            closeBtn.setAttribute('aria-label', 'Close');
            closeBtn.style.position = 'absolute';
            closeBtn.style.top = '6px';
            closeBtn.style.right = '8px';
            closeBtn.style.width = '28px';
            closeBtn.style.height = '28px';
            closeBtn.style.borderRadius = '50%';
            closeBtn.style.border = 'none';
            closeBtn.style.background = 'rgba(0,0,0,0.6)';
            closeBtn.style.color = '#fff';
            closeBtn.style.cursor = 'pointer';
            closeBtn.style.backfaceVisibility = 'hidden';
            closeBtn.style.webkitBackfaceVisibility = 'hidden';
            container.appendChild(closeBtn);
            const restore = () => {
                isPlaying = false;
                container.innerHTML = '';
                container.appendChild(thumb);
                container.appendChild(overlay);
                container.appendChild(title);
                container.dispatchEvent(new CustomEvent('video-ended', { detail: { id: videoData.id }, bubbles: true }));
            };
            closeBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                restore();
            });
            // Simpler approach: rely on user gesture inside iframe to start video
        };
        const onActivate = (e) => {
            e.stopPropagation();
            // Just replace with iframe; user can press play in the embedded UI
            playVideo();
        };
        // Use pointerdown to avoid conflicts with controls; fallback to click
        container.addEventListener('pointerdown', onActivate);
        container.addEventListener('click', onActivate);
        container.addEventListener('touchstart', onActivate, { passive: true });
        return container;
    }
    static createKeywordElement(keywordData) {
        const element = document.createElement('div');
        element.classList.add('css3d-chip');
        element.style.pointerEvents = 'auto';
        element.style.width = '120px';
        element.style.height = '40px';
        element.style.backgroundColor = `hsl(${Math.random() * 360}, 70%, 50%)`;
        element.style.color = 'white';
        element.style.display = 'flex';
        element.style.alignItems = 'center';
        element.style.justifyContent = 'center';
        element.style.borderRadius = '20px';
        element.style.fontSize = '14px';
        element.style.fontWeight = 'bold';
        element.style.cursor = 'pointer';
        element.style.border = '2px solid rgba(255,255,255,0.3)';
        element.textContent = keywordData.keyword;
        element.style.backfaceVisibility = 'hidden';
        element.style.webkitBackfaceVisibility = 'hidden';
        element.style.transformStyle = 'preserve-3d';
        element.style.willChange = 'transform';
        // Allow click to dispatch custom event for filtering
        element.addEventListener('click', () => {
            const event = new CustomEvent('keyword-click', {
                detail: { keyword: keywordData.keyword },
                bubbles: true
            });
            element.dispatchEvent(event);
        });
        // Flip hint badge for keywords
        const hint = document.createElement('div');
        hint.className = 'flip-hint';
        hint.textContent = 'Rotate to view';
        hint.style.position = 'absolute';
        hint.style.top = '-10px';
        hint.style.right = '-10px';
        hint.style.padding = '2px 6px';
        hint.style.borderRadius = '4px';
        hint.style.fontSize = '10px';
        hint.style.background = 'rgba(0,0,0,0.6)';
        hint.style.color = '#fff';
        hint.style.pointerEvents = 'none';
        hint.style.display = 'none';
        element.style.position = 'relative';
        element.appendChild(hint);
        return element;
    }
    static setupWindowResize(camera, renderer, css3dRenderer) {
        const onWindowResize = () => {
            if (camera instanceof THREE.PerspectiveCamera) {
                camera.aspect = window.innerWidth / window.innerHeight;
                camera.updateProjectionMatrix();
            }
            renderer.setSize(window.innerWidth, window.innerHeight);
            if (css3dRenderer) {
                css3dRenderer.setSize(window.innerWidth, window.innerHeight);
            }
        };
        window.addEventListener('resize', onWindowResize, false);
        return onWindowResize;
    }
    static animate(scene, camera, renderer, controls, css3dRenderer) {
        const animate = () => {
            requestAnimationFrame(animate);
            controls.update();
            if (css3dRenderer) {
                css3dRenderer.render(scene, camera);
            }
            else {
                renderer.render(scene, camera);
            }
        };
        animate();
    }
    static disposeScene(scene) {
        scene.traverse((object) => {
            if (object instanceof THREE.Mesh) {
                if (object.geometry)
                    object.geometry.dispose();
                if (object.material) {
                    if (Array.isArray(object.material)) {
                        object.material.forEach(material => material.dispose());
                    }
                    else {
                        object.material.dispose();
                    }
                }
            }
        });
    }
}
